import React, { useCallback } from 'react'
import type { Entry } from 'contentful'

import RichText from '@/components/ui/rich-text'
import DynamicAction, { type DynamicActionProps } from '@/components/ui/dynamic-action'
import HeadingWithAnchor from '@/components/heading-with-anchor'
import { cx } from '@/utils/strings'
import { useInspectorProps, type InspectorTags } from '@/utils/live-preview'
import type { TypeSectionSetOfCardsSkeleton } from '@/types/ctf'

type SetOfCardsActions = Entry<TypeSectionSetOfCardsSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>['fields']['actions']

export type SectionHeadingProps = {
  headline?: string
  body?: any
  headingLayout?: 'Centered' | 'Left-Aligned'
  columnCount?: number
  isHero?: boolean
  eyebrowText?: string
  hideHeadline?: boolean
  actions?: SetOfCardsActions
  anchorLinkTitle?: string
  pageOrderNo?: number
  entryId?: string
}

export default function SectionHeading({
  headline,
  body,
  headingLayout = 'Centered',
  columnCount = 1,
  isHero,
  eyebrowText,
  hideHeadline = false,
  actions,
  anchorLinkTitle,
  pageOrderNo,
  entryId,
}: SectionHeadingProps) {
  const inspectorProps = useInspectorProps({ entryId })

  const HeadingWrap = useCallback(
    ({ children, ...rest }: { children: string } & InspectorTags) => {
      if (isHero) {
        return (
          <h1 className="text-center header-gradient text-balance h1" {...rest}>
            {children}
          </h1>
        )
      }
      if (pageOrderNo === 1) {
        return anchorLinkTitle ? (
          <HeadingWithAnchor
            type="h2"
            title={children}
            id={anchorLinkTitle}
            className="h3 relative inline-block header-gradient"
            {...rest}
          />
        ) : (
          <h2 className="text-center h3 header-gradient text-balance" {...rest}>
            {children}
          </h2>
        )
      }
      return anchorLinkTitle ? (
        <HeadingWithAnchor
          type="h3"
          title={children}
          id={anchorLinkTitle}
          className="h3 header-gradient relative inline-block"
          {...rest}
        />
      ) : (
        <h3
          className={cx(
            'text-balance header-gradient',
            headingLayout === 'Centered' && 'h3',
            headingLayout === 'Left-Aligned' && 'h4 text-left py-0'
          )}
          {...rest}
        >
          {children}
        </h3>
      )
    },
    [anchorLinkTitle, isHero, pageOrderNo, headingLayout]
  )

  const renderActions = useCallback(
    () => (
      <div
        className={cx('flex flex-wrap items-center gap-4', headingLayout === 'Centered' && 'justify-center')}
        {...inspectorProps({ fieldId: 'actions' })}
      >
        {actions?.map((action) => {
          if (!action) return null
          const type = action.sys.contentType.sys.id
          const fields = {
            type,
            ...action.fields,
          } as DynamicActionProps
          return <DynamicAction key={action?.sys.id} id={action?.sys.id} {...fields} data-testid="card-cta" />
        })}
      </div>
    ),
    [actions, headingLayout]
  )

  if (!headline?.length && !body) return null

  return (
    <div
      className={cx(
        headingLayout === 'Centered' && 'flex flex-col gap-4 items-center',
        headingLayout === 'Left-Aligned' && 'flex flex-col gap-4 md:gap-8',
        columnCount >= 2 && 'pb-6 lg:pb-12',
        isHero && 'py-20'
      )}
    >
      <div
        className={cx(
          headingLayout === 'Centered' && 'text-center max-w-6xl mx-auto px-5',
          headingLayout === 'Left-Aligned' && 'flex flex-col gap-2 md:gap-8 md:flex-row md:items-center'
        )}
      >
        <div className="flex-1">
          {eyebrowText && (
            <p className="text-eyebrow uppercase" {...inspectorProps({ fieldId: 'eyebrowText' })}>
              {eyebrowText}
            </p>
          )}
          {headline && !hideHeadline && (
            <HeadingWrap {...inspectorProps({ fieldId: 'headline' })}>{headline}</HeadingWrap>
          )}
        </div>
        {body && (
          <div className={cx('flex-1', !actions && 'md:pt-4')} {...inspectorProps({ fieldId: 'body' })}>
            <RichText
              className={cx(
                '[&_p]:text-base xl:[&_p]:text-lg [&_p]:last:mb-0 [&_p]:text-balance',
                headingLayout === 'Left-Aligned' && 'md:[&_p]:text-right'
              )}
              body={body}
            />
          </div>
        )}
      </div>
      {actions && renderActions()}
    </div>
  )
}
